import React from 'react';
import { Icon } from '@iconify/react';

export type AdminAccessStatus = 'active' | 'inactive' | 'blocked' | 'invited';

export const ADMIN_ACCESS_STATUS: Record<AdminAccessStatus, {
  label: string;
  description: string;
  dot: string;
  tone: string;
}> = {
  active: {
    label: 'Active',
    description: 'Available for normal workspace use',
    dot: 'bg-emerald-400',
    tone: 'border-emerald-400/20 bg-emerald-400/[0.08] text-emerald-200',
  },
  invited: {
    label: 'Invited',
    description: 'Waiting for account activation',
    dot: 'bg-sky-400',
    tone: 'border-sky-400/20 bg-sky-400/[0.08] text-sky-200',
  },
  inactive: {
    label: 'Inactive',
    description: 'Not currently in active use',
    dot: 'bg-white/35',
    tone: 'border-white/10 bg-white/[0.04] text-white/55',
  },
  blocked: {
    label: 'Blocked',
    description: 'Access restriction applied',
    dot: 'bg-rose-400',
    tone: 'border-rose-400/20 bg-rose-400/[0.08] text-rose-200',
  },
};

export const normalizeAdminAccessStatus = (
  value: unknown,
  fallback: AdminAccessStatus = 'active',
): AdminAccessStatus => {
  if (typeof value !== 'string') return fallback;
  const normalized = value.trim().toLowerCase();
  return normalized in ADMIN_ACCESS_STATUS ? normalized as AdminAccessStatus : fallback;
};

export const AccessStatusSelect: React.FC<{
  value: AdminAccessStatus;
  onChange: (value: AdminAccessStatus) => void;
  disabled?: boolean;
  busy?: boolean;
  label?: string;
  className?: string;
}> = ({ value, onChange, disabled = false, busy = false, label = 'Access status', className = '' }) => {
  const status = ADMIN_ACCESS_STATUS[value];
  return (
    <span className={`relative inline-flex min-w-[118px] items-center ${className}`}>
      <span className={`pointer-events-none absolute left-3 z-10 h-1.5 w-1.5 rounded-full ${status.dot} ${value === 'active' ? 'shadow-[0_0_10px_rgba(52,211,153,0.7)]' : ''}`} />
      <select
        value={value}
        onChange={(event) => onChange(event.target.value as AdminAccessStatus)}
        disabled={disabled || busy}
        aria-label={label}
        title={status.description}
        className={`h-9 w-full cursor-pointer appearance-none rounded-full border py-0 pl-7 pr-8 text-[10px] font-black uppercase tracking-[0.14em] outline-none transition-all hover:brightness-125 focus:ring-2 focus:ring-[#5260FF]/30 disabled:cursor-not-allowed disabled:opacity-60 ${status.tone}`}
      >
        {(Object.keys(ADMIN_ACCESS_STATUS) as AdminAccessStatus[]).map((key) => (
          <option key={key} value={key}>{ADMIN_ACCESS_STATUS[key].label}</option>
        ))}
      </select>
      <Icon
        icon={busy ? 'bx:loader-circle' : 'bx:chevron-down'}
        className={`pointer-events-none absolute right-2.5 h-3.5 w-3.5 text-current/60 ${busy ? 'animate-spin' : ''}`}
      />
    </span>
  );
};

interface ImpersonateButtonProps {
  userName: string;
  onClick: () => void;
  className?: string;
}

export const ImpersonateButton: React.FC<ImpersonateButtonProps> = ({
  userName,
  onClick,
  className = '',
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={`Impersonate ${userName}`}
    title={`Impersonate ${userName}`}
    className={`inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-xl border border-[#6673ff]/30 bg-[#5260FF]/10 px-3 text-[8px] font-black uppercase tracking-[0.1em] text-[#aeb5ff] shadow-[inset_0_1px_0_rgba(255,255,255,0.04)] transition-all hover:border-[#7783ff]/55 hover:bg-[#5260FF]/20 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#6673ff]/45 ${className}`}
  >
    <Icon icon="solar:eye-linear" className="h-3.5 w-3.5" />
    <span>Impersonate</span>
  </button>
);

/**
 * Reusable admin-command atoms: toggle switch, campaign status chips,
 * horizontal bar chart, and label chips. Dark design system
 * (#0A0A0A / white borders / #5260FF accent / rounded-3xl).
 */

// ─── ToggleSwitch ────────────────────────────────────────────────────────────

interface ToggleSwitchProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  label?: string;
  description?: string;
  icon?: string;
  disabled?: boolean;
  /** Compact variant for table rows (no label block). */
  compact?: boolean;
}

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({
  checked,
  onChange,
  label,
  description,
  icon,
  disabled = false,
  compact = false,
}) => (
  <label
    className={`flex ${compact ? 'items-center gap-2' : 'items-start justify-between gap-4'} ${disabled ? 'opacity-40 pointer-events-none' : 'cursor-pointer'}`}
  >
    {!compact && (
      <span className="flex min-w-0 items-start gap-2.5">
        {icon && (
          <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl border ${checked ? 'border-[#5260FF]/30 bg-[#5260FF]/10 text-[#5260FF]' : 'border-white/10 bg-white/[0.03] text-white/35'}`}>
            <Icon icon={icon} className="h-4 w-4" />
          </span>
        )}
        <span className="min-w-0">
          {label && <span className="block text-[13px] font-bold text-white">{label}</span>}
          {description && <span className="mt-0.5 block text-[11px] leading-[1.45] text-gray-500">{description}</span>}
        </span>
      </span>
    )}
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={(e) => {
        e.preventDefault();
        onChange(!checked);
      }}
      className={`relative h-6 w-11 shrink-0 rounded-full border transition-all duration-200 ${checked ? 'border-[#5260FF]/50 bg-[#5260FF]' : 'border-white/10 bg-white/[0.06]'}`}
    >
      <span
        className={`absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full bg-white shadow transition-all duration-200 ${checked ? 'left-[24px]' : 'left-[3px]'}`}
      />
    </button>
  </label>
);

// ─── Campaign status chips ───────────────────────────────────────────────────

export const CAMPAIGN_STATUS_STYLE = {
  active: { dot: 'bg-emerald-400', text: 'text-emerald-300', chip: 'border-emerald-400/20 bg-emerald-400/10' },
  paused: { dot: 'bg-amber-400', text: 'text-amber-300', chip: 'border-amber-400/20 bg-amber-400/10' },
  archived: { dot: 'bg-white/30', text: 'text-white/45', chip: 'border-white/10 bg-white/[0.04]' },
} as const;

export type CampaignStatusKey = keyof typeof CAMPAIGN_STATUS_STYLE;

interface StatusChipsProps {
  counts: { active: number; paused: number; archived: number } | undefined;
  /** When false, only statuses with count > 0 are rendered. */
  showZero?: boolean;
}

export const StatusChips: React.FC<StatusChipsProps> = ({ counts, showZero = false }) => {
  if (!counts) return <span className="text-[11px] text-white/25">—</span>;
  return (
    <span className="flex flex-wrap items-center gap-1.5">
      {(Object.keys(CAMPAIGN_STATUS_STYLE) as CampaignStatusKey[]).map((key) => {
        const value = counts[key];
        if (!showZero && value === 0) return null;
        const style = CAMPAIGN_STATUS_STYLE[key];
        return (
          <span
            key={key}
            className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${style.chip} ${style.text}`}
            title={`${key}: ${value}`}
          >
            <span className={`h-1 w-1 rounded-full ${style.dot}`} />
            {value} {key}
          </span>
        );
      })}
      {counts.active === 0 && counts.paused === 0 && counts.archived === 0 && (
        <span className="text-[11px] text-white/25">No campaigns</span>
      )}
    </span>
  );
};

// ─── Horizontal bar chart (credits per client) ──────────────────────────────

export interface BarDatum {
  id: string;
  label: string;
  value: number;
  /** Rendered to the right of the bar (e.g. campaign chips). */
  detail?: React.ReactNode;
}

interface CreditsBarChartProps {
  data: BarDatum[];
  valueFormat?: (value: number) => string;
  emptyLabel?: string;
}

/**
 * Pure CSS horizontal bars — no chart dependency. Widths proportional to the
 * max value; the top bar uses the brand accent, the rest fade by rank.
 */
export const CreditsBarChart: React.FC<CreditsBarChartProps> = ({
  data,
  valueFormat = (v) => v.toLocaleString(),
  emptyLabel = 'No data',
}) => {
  if (data.length === 0) {
    return <div className="flex h-24 items-center justify-center text-xs text-white/25">{emptyLabel}</div>;
  }
  const max = Math.max(...data.map((d) => d.value), 1);

  return (
    <div className="space-y-2">
      {data.map((datum, index) => (
        <div key={datum.id} className="flex items-center gap-3">
          <span className="w-28 shrink-0 truncate text-right text-[11px] font-semibold text-white/60" title={datum.label}>
            {datum.label}
          </span>
          <div className="h-6 min-w-0 flex-1 overflow-hidden rounded-lg bg-white/[0.03]">
            <div
              className={`flex h-full items-center justify-end rounded-lg pr-2 transition-all duration-500 ${index === 0 ? 'bg-[#5260FF]' : index === 1 ? 'bg-[#5260FF]/70' : 'bg-[#5260FF]/40'}`}
              style={{ width: `${Math.max((datum.value / max) * 100, 8)}%` }}
            >
              <span className={`whitespace-nowrap text-[10px] font-black tracking-wide ${index === 0 ? 'text-white' : 'text-white/80'}`}>
                {valueFormat(datum.value)}
              </span>
            </div>
          </div>
          {datum.detail && <span className="hidden shrink-0 items-center gap-2 md:flex">{datum.detail}</span>}
        </div>
      ))}
    </div>
  );
};

// ─── Label chips (user tags) ─────────────────────────────────────────────────

export const LabelChip: React.FC<{
  label: string;
  onRemove?: () => void;
  tone?: 'admin' | 'default';
}> = ({ label, onRemove, tone = 'default' }) => (
  <span
    className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${
      tone === 'admin'
        ? 'border-red-500/20 bg-red-500/15 text-red-300'
        : 'border-cyan-500/15 bg-cyan-500/10 text-cyan-300/70'
    }`}
  >
    {label}
    {onRemove && (
      <button
        type="button"
        onClick={onRemove}
        className="cursor-pointer text-current/60 transition-colors hover:text-white"
        aria-label={`Remove label ${label}`}
      >
        <Icon icon="bx:x" className="h-3 w-3" />
      </button>
    )}
  </span>
);

// ── MemberAvatar ─────────────────────────────────────────────────────────────
// Shared avatar for team members: renders the member's profile picture and
// falls back to their initials when the URL is absent OR broken (failed load).
// Used by Team Members list, campaign assignment, and anywhere a member photo
// appears in Team view — one fallback rule everywhere.

export const getMemberInitials = (name?: string | null, email?: string | null): string => {
  const source = (name || email || '').trim();
  if (!source) return '?';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  return (parts[0]?.slice(0, 2) || '?').toUpperCase();
};

/** True only for a real http(s) URL — stored "null" strings, empty values and
 *  relative junk would otherwise render as a broken-image icon. */
export const isUsableImageUrl = (value: unknown): value is string =>
  typeof value === 'string'
  && /^https?:\/\//i.test(value.trim())
  && value.trim().toLowerCase() !== 'null';

interface MemberAvatarProps {
  name?: string | null;
  email?: string | null;
  imageUrl?: string | null;
  className?: string;
}

export const MemberAvatar: React.FC<MemberAvatarProps> = ({
  name,
  email,
  imageUrl,
  className = '',
}) => {
  const [imageFailed, setImageFailed] = React.useState(false);
  const hasImage = isUsableImageUrl(imageUrl) && !imageFailed;
  React.useEffect(() => {
    // Reset the failure flag when a member gets a new picture URL.
    setImageFailed(false);
  }, [imageUrl]);

  return (
    <span className={`flex shrink-0 items-center justify-center overflow-hidden bg-[#181b24] text-[11px] font-bold text-[#a3aed3] ${className}`}>
      {hasImage ? (
        <img
          src={imageUrl as string}
          alt=""
          loading="lazy"
          onError={() => setImageFailed(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        getMemberInitials(name, email)
      )}
    </span>
  );
};
